<div class="space-y-8 pb-10">
  <div class="flex items-center justify-between">
    <div>
      <h1 class="text-3xl font-bold tracking-tight">Projects</h1>
      <p class="text-slate-600 mt-2">Manage your real estate projects</p>
    </div>
    <button
      type="button"
      (click)="openCreate()"
      [attr.aria-disabled]="!canWriteProjects()"
      [title]="
        canWriteProjects()
          ? 'Create a new project'
          : 'Your access is restricted. Contact your administrator to enable this action.'
      "
      class="inline-flex items-center gap-2 rounded-lg bg-[color:var(--primary)] px-4 py-2 text-[color:var(--primary-foreground)] shadow-sm transition hover:bg-[color:var(--primary-hover)]"
      [ngClass]="
        !canWriteProjects()
          ? 'cursor-not-allowed opacity-50 hover:bg-[color:var(--primary)]'
          : ''
      "
    >
      <lucide-icon name="plus" class="h-4 w-4"></lucide-icon>
      New Project
    </button>
  </div>

  <ng-container *ngIf="!loading && !error; else stateTpl">
    <div
      *ngIf="projects.length === 0; else listTpl"
      class="rounded-2xl border border-slate-200 bg-white shadow-sm"
    >
      <div class="py-16 text-center px-6">
        <lucide-icon
          name="building-2"
          class="mx-auto mb-4 h-16 w-16 text-[#3a4e49]/50"
        ></lucide-icon>
        <h3 class="text-xl font-semibold text-slate-900 mb-2">
          No projects yet
        </h3>
        <p class="text-slate-600 mb-6 max-w-md mx-auto">
          Get started by creating your first real estate project. You'll be able
          to add properties, manage details, and track progress.
        </p>
        <button
          type="button"
          (click)="openCreate()"
          [attr.aria-disabled]="!canWriteProjects()"
          [title]="
            canWriteProjects()
              ? 'Create your first project'
              : 'Your access is restricted. Contact your administrator to enable this action.'
          "
          class="inline-flex items-center gap-2 rounded-lg bg-[color:var(--primary)] px-4 py-2 text-[color:var(--primary-foreground)] transition hover:bg-[color:var(--primary-hover)]"
          [ngClass]="
            !canWriteProjects()
              ? 'cursor-not-allowed opacity-50 hover:bg-[color:var(--primary)]'
              : ''
          "
        >
          <lucide-icon name="plus" class="h-4 w-4"></lucide-icon>
          {{
            canWriteProjects()
              ? "Create Your First Project"
              : "Project creation restricted"
          }}
        </button>
      </div>
    </div>
  </ng-container>

  <ng-template #listTpl>
    <div
      class="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 shadow-sm mb-4"
    >
      <div class="flex items-center gap-2">
        <span class="font-semibold"
          >Showing {{ projects.length }} of {{ totalCount }} projects</span
        >
        <span *ngIf="loadingPage" class="text-[11px] text-slate-500"
          >(Loading...)</span
        >
      </div>
    </div>
    <div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      <div
        *ngFor="let project of projects"
        class="min-w-0 rounded-2xl border border-slate-200 bg-white hover:border-[color:var(--ring)] transition-all shadow-[0_22px_50px_rgba(15,23,42,0.10)] overflow-hidden h-full flex flex-col"
      >
        <div
          class="relative h-36 overflow-hidden bg-slate-100 border-b border-slate-200"
        >
          <img
            *ngIf="
              project.image && !projectImageFailed[project.id];
              else projectHeroFallback
            "
            [src]="project.image"
            [alt]="project.name || 'Project image'"
            class="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
            (error)="onProjectImageError(project.id)"
          />
          <ng-template #projectHeroFallback>
            <div
              class="flex h-full w-full items-center justify-center text-slate-400"
            >
              <lucide-icon name="image" class="h-8 w-8"></lucide-icon>
            </div>
          </ng-template>
          <span
            *ngIf="showProjectStatusBadge(project)"
            class="absolute left-3 top-3 rounded-full bg-black/60 px-3 py-1 text-[11px] font-semibold text-white/80 border border-white/10"
          >
            {{ statusLabel(project.status) }}
          </span>
        </div>
        <div class="p-5 gap-4 h-full flex flex-col flex-1">
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-center gap-2">
                <h3 class="text-lg font-semibold text-slate-900 break-words">
                  {{ project.name || "Untitled Project" }}
                </h3>
                <span
                  *ngIf="project.isVerified"
                  class="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-[11px] font-semibold text-emerald-700"
                >
                  <lucide-icon
                    name="shield-check"
                    class="h-3.5 w-3.5"
                  ></lucide-icon>
                  Verified
                </span>
              </div>
              <ng-container *ngIf="project.locationLabel || project.location">
                <a
                  *ngIf="project.locationLink; else projectLocationText"
                  [href]="project.locationLink"
                  target="_blank"
                  rel="noreferrer"
                  class="flex min-w-0 items-center gap-2 text-sm text-slate-600 hover:text-[color:var(--primary)]"
                >
                  <lucide-icon
                    name="map-pin"
                    class="h-4 w-4 shrink-0 text-[#6e9bad]"
                  ></lucide-icon>
                  <span class="truncate min-w-0 flex-1">{{
                    project.locationLabel || project.location
                  }}</span>
                  <lucide-icon
                    name="external-link"
                    class="h-3.5 w-3.5 shrink-0 text-slate-400"
                  ></lucide-icon>
                </a>
                <ng-template #projectLocationText>
                  <p class="flex items-center gap-2 text-sm text-slate-600">
                    <lucide-icon
                      name="map-pin"
                      class="h-4 w-4 shrink-0 text-[#6e9bad]"
                    ></lucide-icon>
                    <span class="truncate min-w-0 flex-1">{{
                      project.locationLabel || project.location
                    }}</span>
                  </p>
                </ng-template>
              </ng-container>
              <p
                *ngIf="project.countryName || project.country"
                class="text-xs text-slate-500 mt-1"
              >
                {{ project.countryName || project.country }}
              </p>
              <div
                *ngIf="developerName(project) || project.organizationName"
                class="mt-3 flex items-center gap-2"
              >
                <div
                  class="h-9 w-9 overflow-hidden rounded-full border border-slate-200 bg-slate-50 flex items-center justify-center"
                >
                  <img
                    *ngIf="
                      project.vendorLogo && !partnerLogoFailed[project.id];
                      else partnerLogoFallback
                    "
                    [src]="project.vendorLogo"
                    [alt]="
                      project.organizationName ||
                      developerName(project) ||
                      'Partner'
                    "
                    class="h-full w-full object-cover"
                    (error)="onPartnerLogoError(project.id)"
                  />
                  <ng-template #partnerLogoFallback>
                    <lucide-icon
                      name="users"
                      class="h-4 w-4 text-slate-400"
                    ></lucide-icon>
                  </ng-template>
                </div>
                <div class="min-w-0">
                  <p
                    *ngIf="developerName(project)"
                    class="text-xs font-semibold text-slate-700 truncate"
                  >
                    {{ developerName(project) }}
                  </p>
                  <p
                    *ngIf="
                      project.organizationName &&
                      project.organizationName !== developerName(project)
                    "
                    class="text-[11px] text-slate-500 truncate"
                  >
                    {{ project.organizationName }}
                  </p>
                </div>
              </div>
            </div>
            <span
              *ngIf="showProjectStatusBadge(project)"
              class="shrink-0 rounded-full bg-[#aa9072]/10 text-[#aa9072] border border-[#aa9072]/25 text-xs font-semibold px-3 py-1"
            >
              {{ statusLabel(project.status) }}
            </span>
          </div>

          <p
            class="text-sm text-slate-600 line-clamp-3"
            *ngIf="project.description; else noDesc"
          >
            {{ project.description }}
          </p>
          <ng-template #noDesc>
            <p class="text-sm text-slate-500 italic">
              No description available
            </p>
          </ng-template>

          <div class="flex flex-wrap gap-2">
            <span
              *ngFor="let highlight of project.highlights || []"
              class="rounded-full bg-slate-100 border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700"
            >
              {{ highlight }}
            </span>
            <span
              *ngIf="project.propertyCount"
              class="rounded-full bg-slate-100 border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700"
            >
              {{ project.propertyCount }} units
            </span>
          </div>

          <div
            class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-700"
          >
            <div
              class="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2"
            >
              <lucide-icon
                name="calendar-clock"
                class="h-4 w-4 text-[#6e9bad]"
              ></lucide-icon>
              <div>
                <p class="text-[11px] uppercase tracking-wide text-slate-500">
                  Delivery
                </p>
                <p class="font-semibold">
                  {{ project.deliveryStatus || "Schedule TBD" }}
                </p>
              </div>
            </div>
            <div
              class="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2"
            >
              <lucide-icon
                name="wallet"
                class="h-4 w-4 text-[#6e9bad]"
              ></lucide-icon>
              <div>
                <p class="text-[11px] uppercase tracking-wide text-slate-500">
                  Starting price
                </p>
                <p class="font-semibold">
                  {{ startingPriceLabel(project) || "Contact for pricing" }}
                </p>
              </div>
            </div>
          </div>

          <div class="mt-auto">
            <a
              class="inline-flex w-full items-center justify-center rounded-md bg-[color:var(--primary)] px-4 py-2 text-sm font-semibold text-[color:var(--primary-foreground)] transition-colors hover:bg-[color:var(--primary-hover)]"
              [routerLink]="['/developer/projects', project.id]"
            >
              Manage Project
            </a>
          </div>
        </div>
      </div>
    </div>

    <div *ngIf="hasNextPage" class="mt-6 flex justify-center">
      <button
        type="button"
        (click)="showMore()"
        [disabled]="loadingPage"
        class="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
      >
        <lucide-icon
          *ngIf="loadingPage"
          name="loader-2"
          class="h-4 w-4 animate-spin"
        ></lucide-icon>
        <span>{{ loadingPage ? "Loading…" : "Show more" }}</span>
      </button>
    </div>
  </ng-template>

  <ng-template #stateTpl>
    <div *ngIf="loading">
      <div
        class="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm mb-4"
        aria-hidden="true"
      >
        <div class="h-4 w-48 rounded bg-slate-200/70 animate-pulse"></div>
      </div>
      <app-loading-spinner type="project-cards" [count]="6"></app-loading-spinner>
    </div>
    <div
      *ngIf="!loading && error"
      class="rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm"
    >
      <p class="text-red-600 font-medium mb-4">{{ error }}</p>
      <button
        type="button"
        (click)="reload()"
        class="inline-flex items-center gap-2 rounded-lg bg-[color:var(--primary)] px-4 py-2 text-[color:var(--primary-foreground)] transition hover:bg-[color:var(--primary-hover)]"
      >
        Retry
      </button>
    </div>
  </ng-template>
</div>

<div
  *ngIf="showCreate"
  class="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 backdrop-blur px-4 py-8 overflow-y-auto"
>
  <div
    class="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-6 text-slate-900 shadow-2xl max-h-[90vh] overflow-y-auto"
  >
    <div class="flex items-center justify-between mb-4">
      <div>
        <h3 class="text-xl font-semibold">Create Project</h3>
        <p class="text-sm text-slate-600">
          Add a new project with basic details
        </p>
      </div>
      <button
        type="button"
        (click)="closeCreate()"
        [disabled]="saving"
        class="rounded-full bg-slate-100 p-2 hover:bg-slate-200"
      >
        <lucide-icon name="x" class="h-4 w-4"></lucide-icon>
      </button>
    </div>
    <form
      [formGroup]="createForm"
      class="space-y-4"
      (ngSubmit)="submitCreate()"
    >
      <div class="grid gap-3">
        <label class="flex flex-col gap-2 text-sm text-slate-700">
          Project Name <span class="text-red-600">*</span>
          <input
            formControlName="name"
            class="rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 focus:border-[#6e9bad] focus:outline-none"
            placeholder="Project name"
          />
        </label>
        <label class="flex flex-col gap-2 text-sm text-slate-700">
          Description
          <textarea
            formControlName="description"
            rows="3"
            class="rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 focus:border-[#6e9bad] focus:outline-none"
            placeholder="Short description"
          ></textarea>
        </label>
        <div class="grid gap-3 sm:grid-cols-2">
          <label class="flex flex-col gap-2 text-sm text-slate-700">
            Development Badge
            <select
              formControlName="developmentBadgeMode"
              class="rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-900 focus:border-[#6e9bad] focus:outline-none"
            >
              <option value="new_launch">New launch</option>
              <option value="development_off_plan">
                New Development/Off-Plan
              </option>
              <option value="off_plan">Off-plan</option>
              <option value="under_construction">Under construction</option>
              <option value="funding">Funding</option>
              <option value="custom">Custom</option>
            </select>
          </label>
          <label
            *ngIf="createForm.value.developmentBadgeMode === 'custom'"
            class="flex flex-col gap-2 text-sm text-slate-700"
          >
            Custom Badge Label
            <input
              formControlName="developmentBadgeCustom"
              type="text"
              maxlength="60"
              class="rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 focus:border-[#6e9bad] focus:outline-none"
              placeholder="e.g. Limited units"
            />
          </label>
        </div>
        <div class="space-y-2">
          <p class="text-sm font-semibold text-slate-900">Location</p>
          <div class="grid gap-3 sm:grid-cols-2">
            <label class="flex flex-col gap-2 text-sm text-slate-700">
              Country <span class="text-red-600">*</span>
              <app-country-selector
                formControlName="country"
              ></app-country-selector>
            </label>
            <label
              class="flex flex-col gap-2 text-sm text-slate-700 sm:col-span-2"
            >
              Location Address
              <input
                formControlName="locationAddress"
                class="rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 focus:border-[#6e9bad] focus:outline-none"
                placeholder="Shown on public project cards"
              />
            </label>
            <label
              class="flex flex-col gap-2 text-sm text-slate-700 sm:col-span-2"
            >
              Location Link (Google Maps)
              <input
                formControlName="locationLink"
                class="rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 focus:border-[#6e9bad] focus:outline-none"
                placeholder="https://maps.google.com/..."
              />
            </label>
          </div>
          <p class="text-xs text-slate-500">
            Optional: add a specific address and a Google Maps link for the
            public cards.
          </p>
        </div>
      </div>

      <div class="space-y-3">
        <p class="text-sm font-semibold text-slate-900">Project Image</p>
        <div
          class="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4"
        >
          <div class="flex items-center gap-3">
            <div
              class="h-20 w-32 overflow-hidden rounded-lg border border-slate-200 bg-white"
            >
              <img
                *ngIf="projectImagePreview"
                [src]="projectImagePreview"
                alt="Project preview"
                class="h-full w-full object-cover"
              />
              <div
                *ngIf="!projectImagePreview"
                class="flex h-full items-center justify-center text-xs text-slate-500"
              >
                No image selected
              </div>
            </div>
            <div class="flex-1 space-y-2">
              <p class="text-xs text-slate-600">
                Upload a single cover image (JPG/PNG). Re-uploading replaces the
                previous version.
              </p>
              <div class="flex flex-wrap gap-2">
                <label
                  class="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  <lucide-icon name="upload" class="h-4 w-4"></lucide-icon>
                  <span>{{
                    projectImagePreview ? "Replace image" : "Upload image"
                  }}</span>
                  <input
                    type="file"
                    class="hidden"
                    accept="image/*"
                    (change)="onProjectImageSelected($event)"
                    [disabled]="saving || uploadingProjectImage"
                  />
                </label>
                <button
                  *ngIf="projectImagePreview"
                  type="button"
                  (click)="clearProjectImageSelection()"
                  [disabled]="saving"
                  class="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 transition hover:bg-slate-50"
                >
                  Remove
                </button>
              </div>
              <p *ngIf="imageError" class="text-xs text-red-600">
                {{ imageError }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="space-y-3">
        <p class="text-sm font-semibold text-slate-900">Documents</p>
        <div class="grid gap-3 sm:grid-cols-2">
          <div class="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div class="flex items-start justify-between gap-3">
              <div class="space-y-1">
                <p class="text-sm font-semibold text-slate-900">Brochure</p>
                <p class="text-xs text-slate-600">
                  PDF recommended. Visible on all units in this project.
                </p>
              </div>
              <span
                class="rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-600"
              >
                {{ brochureFileName ? "Selected" : "Optional" }}
              </span>
            </div>
            <div class="mt-3 flex flex-wrap gap-2">
              <label
                class="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                <lucide-icon name="upload" class="h-4 w-4"></lucide-icon>
                <span>{{ brochureFileName ? "Replace" : "Upload" }}</span>
                <input
                  type="file"
                  class="hidden"
                  accept=".pdf,image/*"
                  (change)="onBrochureSelected($event)"
                  [disabled]="saving || !!createdProjectId"
                />
              </label>
              <button
                *ngIf="brochureFileName"
                type="button"
                (click)="clearBrochureSelection()"
                class="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 transition hover:bg-slate-50"
              >
                Remove
              </button>
            </div>
            <p
              *ngIf="brochureFileName"
              class="mt-2 text-xs text-slate-600 truncate"
            >
              {{ brochureFileName }}
            </p>
          </div>

          <div class="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div class="flex items-start justify-between gap-3">
              <div class="space-y-1">
                <p class="text-sm font-semibold text-slate-900">Master Plan</p>
                <p class="text-xs text-slate-600">
                  PDF or image. Visible on all units in this project.
                </p>
              </div>
              <span
                class="rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-600"
              >
                {{ masterPlanFileName ? "Selected" : "Optional" }}
              </span>
            </div>
            <div class="mt-3 flex flex-wrap gap-2">
              <label
                class="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                <lucide-icon name="upload" class="h-4 w-4"></lucide-icon>
                <span>{{ masterPlanFileName ? "Replace" : "Upload" }}</span>
                <input
                  type="file"
                  class="hidden"
                  accept=".pdf,image/*"
                  (change)="onMasterPlanSelected($event)"
                  [disabled]="saving || !!createdProjectId"
                />
              </label>
              <button
                *ngIf="masterPlanFileName"
                type="button"
                (click)="clearMasterPlanSelection()"
                class="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 transition hover:bg-slate-50"
              >
                Remove
              </button>
            </div>
            <p
              *ngIf="masterPlanFileName"
              class="mt-2 text-xs text-slate-600 truncate"
            >
              {{ masterPlanFileName }}
            </p>
          </div>
        </div>
        <p *ngIf="documentsError" class="text-xs text-red-600">
          {{ documentsError }}
        </p>
      </div>

      <div class="space-y-3">
        <p class="text-sm font-semibold text-slate-900">Reservations</p>
        <div class="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <label class="flex items-start justify-between gap-4">
            <div class="space-y-1">
              <p class="text-sm font-semibold text-slate-900">
                Enable reservations for this project
              </p>
              <p class="text-xs text-slate-600">
                Eligible partners can decide project by project whether
                reservation CTAs and reservation creation are available.
              </p>
              <p
                *ngIf="!reservationsFeatureAvailable"
                class="text-xs text-amber-700"
              >
                Reservations are currently disabled for your organization.
                Enable them from Settings after your super admin grants access.
              </p>
            </div>
            <input
              type="checkbox"
              formControlName="reservationsEnabled"
              [disabled]="!reservationsFeatureAvailable || saving"
              class="mt-1 h-4 w-4 rounded border-slate-300 text-[#3a4e49] focus:ring-[#3a4e49]"
            />
          </label>
        </div>
      </div>

      <div class="space-y-3">
        <p class="text-sm font-semibold text-slate-900">Sharing</p>
        <div class="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <label class="flex items-start justify-between gap-4">
            <div class="space-y-1">
              <p class="text-sm font-semibold text-slate-900">
                Share this project with the company
              </p>
              <p class="text-xs text-slate-600">
                When on, everyone in your company can see this project. When off,
                only you (the creator) and people with project edit permission
                can see it. You can always edit a project you created.
              </p>
            </div>
            <input
              type="checkbox"
              formControlName="sharedWithOrg"
              [disabled]="saving || !!createdProjectId"
              class="mt-1 h-4 w-4 rounded border-slate-300 text-[#3a4e49] focus:ring-[#3a4e49]"
            />
          </label>
        </div>
      </div>
      <div class="flex items-center gap-3 justify-end pt-2">
        <button
          type="button"
          (click)="closeCreate()"
          [disabled]="saving"
          class="rounded-lg border border-slate-200 bg-white px-4 py-2 text-slate-700 hover:bg-slate-50"
        >
          {{ createdProjectId ? "Close" : "Cancel" }}
        </button>
        <button
          type="submit"
          [disabled]="saving || (createdProjectId ? !projectImageFile : (createForm.invalid || !!imageError))"
          class="inline-flex items-center gap-2 rounded-lg bg-[color:var(--primary)] px-4 py-2 text-[color:var(--primary-foreground)] shadow-sm transition hover:bg-[color:var(--primary-hover)] disabled:opacity-60"
        >
          <lucide-icon
            *ngIf="saving"
            name="loader-2"
            class="h-4 w-4 animate-spin"
          ></lucide-icon>
          {{ saving ? "Saving..." : createdProjectId ? "Retry Image Upload" : "Create Project" }}
        </button>
      </div>
      <p *ngIf="error" class="text-sm text-red-600">{{ error }}</p>
    </form>
  </div>
</div>
